/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"
import { unstable_batchedUpdates } from "react-dom"
import { useHistory, useRouteMatch } from "react-router"
import Loader from "../../../components/Loader/Loader"
import { iFeatured } from "../../../definitions/Featured"
import {
  iIdParams,
  iInspectorProps,
  iResponse,
} from "../../../definitions/interfaces"
import { get, post } from "../../../store/data/actions"
import SpaceItem from "../../Spaces/Item"
import ExhibitionItem from "../../Exhibitions/Item"
import ArtistItem from "../../Artists/Item"
import ArtPieceItem from "../../ArtPieces/Item"
import {
  SortableContainer,
  SortableElement,
  SortableContainerProps,
  SortableElementProps,
  SortEnd,
} from "react-sortable-hoc"
import Grid from "../../../components/Grid/Grid"
import FeaturedSelection from "./Selection"
import { Link } from "react-router-dom"
import FeaturedFilters from "./Filters"
import { useFeaturedData } from "./useFeaturedData"
import arrayMove from "array-move"
import { store } from "react-notifications-component"
import { notificationOptions } from "../../../api/Definitions"
import DotButton from "../../../components/Buttons/DotButton"

const FeaturedInspector: FunctionComponent<
  iInspectorProps<iFeatured> & { data: any }
> = ({ item, data }) => {
  const [isLoading, setLoading] = useState(false)
  const [_data, setData] = useState(data)
  const _selected = useRef<any[]>(data)

  const history = useHistory()
  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (newIndex !== oldIndex) {
      _selected.current = arrayMove(_selected.current, oldIndex, newIndex)
      setData(_selected.current)
    }
  }

  const handleSelect = useCallback(
    (selectedItem: any) => {
      if (_selected.current.find((x: any) => x._id === selectedItem._id)) {
        _selected.current = _selected.current.filter(
          (x: any) => x._id !== selectedItem._id,
        )
      } else {
        _selected.current = _selected.current.concat(selectedItem)
      }
      setData(_selected.current)
    },
    [_data],
  )

  const handleHistoryBack = (e: React.SyntheticEvent) => {
    e.preventDefault()
    history.goBack()
  }

  const handleSubmit = () => {
    const ids = _selected.current.map((s) => s._id)
    setLoading(true)
    post<iFeatured>({
      link: "/v1/featured/" + item._id,
      data: { data: ids },
    })
      .then(() => {
        store.addNotification({
          title: "Success!",
          message: "Successfully updated!",
          // message: "teodosii@react-notifications-component",
          type: "success",
          ...notificationOptions,
        })
        setLoading(false)
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const { data: selectionData, query, setQuery } = useFeaturedData({
    dataType: item.dataType,
    query: item.query,
  })

  return (
    <>
      <div className="inspector-header">
        <div className="inspector-cover-wrap">
          <div className="inspector-cover-overlay">
            <div className="inspector-headline">
              <Link to="#" onClick={handleHistoryBack}>
                <i className="fa fa-chevron-left"></i>
                <span>Back</span>
              </Link>
              <DotButton
                className="btn-blue submit-button"
                text={"Update"}
                isLoading={isLoading}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="content-header">
        <FeaturedFilters
          dataType={item.dataType}
          query={query}
          setQuery={setQuery}
        />
      </div>
      <div className="flex-grids">
        <div className="col-6 grid-wrap">
          <label>Select</label>
          <FeaturedSelection
            dataType={item.dataType}
            selected={_data}
            onSelect={handleSelect}
            data={selectionData}
          />
        </div>
        <div className="grid-separator"></div>
        <div className="col-6 grid-wrap selected-wrap">
          <label>Selected</label>
          <SortableList
            axis="xy"
            dataType={item.dataType}
            data={_data}
            distance={1}
            onSortEnd={onSortEnd}
            onClick={handleSelect}
          />
        </div>
      </div>
    </>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [item, setItem] = useState<iFeatured>()
  const [data, setData] = useState<any>()
  const match = useRouteMatch<iIdParams>()

  const load = () => {
    Promise.all([
      get<iResponse<iFeatured>>({
        link: "/v1/featured/" + match.params._id,
      }),
      get<iResponse<any>>({
        link: "/v1/featured/" + match.params._id + "/data",
      }),
    ]).then((result) => {
      const [itemResult, dataResult] = result

      unstable_batchedUpdates(() => {
        setItem(itemResult.data)
        setData(dataResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    load()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item ? (
    <FeaturedInspector item={item} data={data} reload={load} />
  ) : null
}

export default InspectorLoader

interface iSortableListProps {
  data: string[]
  dataType: string
  onClick: (item: any) => void
}

const SortableList = SortableContainer(
  ({
    data,
    dataType,
    onClick,
  }: SortableContainerProps & iSortableListProps) => {
    return (
      <div className="grid-view">
        <Grid>
          {data.map((item: any, index: number) => (
            <SortableItem
              key={item._id}
              index={index}
              item={item}
              dataType={dataType}
              onClick={onClick}
            />
          ))}
        </Grid>
      </div>
    )
  },
)

interface iSortableItemProps {
  item: any
  dataType: string
  onClick: (item: any) => void
}

const SortableItem = SortableElement(
  ({ item, dataType, onClick }: SortableElementProps & iSortableItemProps) => {
    switch (dataType) {
      case "Artists":
        return (
          <div className="removeable-item">
            <a
              className={"open-item"}
              href={"http://localhost:3000/artists/" + item.url}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-external-link-alt"></i>
            </a>
            <div
              className={"remove-item"}
              onClick={() => {
                onClick(item)
              }}
            >
              <i className="fas fa-times"></i>
            </div>
            <ArtistItem {...item} />
          </div>
        )
      case "ArtPieces":
        return (
          <div className="content-item">
            <div
              className={"remove-item"}
              onClick={() => {
                onClick(item)
              }}
            >
              <i className="fas fa-plus"></i>
            </div>
            <ArtPieceItem {...item} />
          </div>
        )
      case "Exhibitions":
        return (
          <div className="content-item">
            <div
              className={"remove-item"}
              onClick={() => {
                onClick(item)
              }}
            >
              <i className="fas fa-plus"></i>
            </div>
            <ExhibitionItem {...item} useDefaultImage />
          </div>
        )
      case "Spaces":
        return (
          <div className="content-item">
            <div
              className={"remove-item"}
              onClick={() => {
                onClick(item)
              }}
            >
              <i className="fas fa-plus"></i>
            </div>
            <SpaceItem {...item} />
          </div>
        )
      // case "Tags":
      //   return (
      //     <div className="content-item">
      //       <div
      //         className={"remove-item"}
      //         onClick={() => {
      //           onClick(item)
      //         }}
      //       >
      //         <i className="fas fa-plus"></i>
      //       </div>
      //       <TagItem {...item} />
      //     </div>
      //   )
      default:
        return <div></div>
    }
  },
)
